import { Injectable, EventEmitter } from '@angular/core';
import { from, Observable, of, Subscriber } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { StorageService } from '../storage/storage.service';
import { BiometricService } from '../../services/biometric.service';

export interface Credential {
  name: string;
  userId: string;
  userContextId?: string;
  contextId?: string;
  login: string;
  accessToken: string;
  refreshToken: string;
  temporaryPassword: boolean;
  cometChatAuthToken?: string;
  cometChatUid?: string;
  profile?: string;
}

export interface RefreshEvent {
  next: (credential: Credential) => void;
  error: (error: any) => void;
  credential: Credential;
}

export interface UnauthorizedEvent {
  [key: string]: string;
}

@Injectable({
  providedIn: 'root',
})
export class SecurityService {
  private current: Credential;

  private refreshQueue: Subscriber<Credential>[];

  public readonly onInit: EventEmitter<void>;

  public readonly onInvalidate: EventEmitter<void>;

  public readonly onRefresh: EventEmitter<RefreshEvent>;

  public readonly onUnauthorized: EventEmitter<UnauthorizedEvent>;

  constructor(
    private storageService: StorageService,
    private biometricService: BiometricService
  ) {
    this.refreshQueue = [];

    this.onInit = new EventEmitter<void>();
    this.onInvalidate = new EventEmitter<void>();
    this.onRefresh = new EventEmitter<RefreshEvent>();
    this.onUnauthorized = new EventEmitter<UnauthorizedEvent>();
  }

  public init(): Observable<Credential> {
    const promise = this.storageService.get('security');

    return from(promise).pipe(
      mergeMap((data: any) => {
        this.current = data;

        if (this.current) {
          return this.emitRefresh();
        }

        this.onInit.emit();
        return of(this.current);
      })
    );
  }

  public setCredential(credential: Credential): Observable<any> {
    const sameUser = this.current?.userId && credential?.userId && this.current.userId === credential.userId;
    const mergedCredential: Credential = sameUser ? {
      ...credential,
      cometChatAuthToken: credential.cometChatAuthToken || this.current?.cometChatAuthToken,
      cometChatUid: credential.cometChatUid || this.current?.cometChatUid,
    } : credential;

    this.current = mergedCredential;
    this.onInit.emit();

    // Mantém o token da biometria atualizado com o refreshToken mais recente
    // (a rotação durante a sessão tornaria o token salvo inválido).
    this.biometricService.syncStoredToken(mergedCredential?.refreshToken);

    const promise = this.storageService.set('security', mergedCredential);
    return from(promise);
  }

  public invalidate(): Observable<void> {
    return new Observable((subscriber: Subscriber<void>) => {
      // "Já viu a introdução" NÃO é limpo aqui: é preferência do usuário, não
      // estado de sessão. Limpar junto fazia as telas de boas-vindas voltarem a
      // cada logout — e também a cada expiração de token, porque invalidate()
      // é chamado pelo app-lock e pelo tratamento de 401. A chave é por userId
      // (intro-session-seen:<userId>), então cada conta mantém a sua marca e
      // um usuário novo no mesmo aparelho continua vendo a introdução.
      const promiseList = [
        this.storageService.remove('security'),
        this.storageService.remove('agent')
      ];

      Promise.all(promiseList).then(() => {
        delete this.current;

        subscriber.next();
        this.onInvalidate.emit();
      });
    });
  }

  public emitUnauthorized(data?: UnauthorizedEvent): void {
    this.onUnauthorized.emit(data);
  }

  public emitRefresh(): Observable<Credential> {
    return new Observable((subscriber: Subscriber<Credential>) => {
      if (this.refreshQueue.length === 0) {
        this.onRefresh.emit({
          credential: this.current,
          next: (credential: Credential) => {
            this.setCredential(credential);
            this.refreshQueue.forEach((data: Subscriber<Credential>) =>
              data.next(credential)
            );
            this.refreshQueue = [];
          },
          error: (error: any) => {
            this.emitUnauthorized();
            this.refreshQueue.forEach((data: Subscriber<Credential>) =>
              data.error(error)
            );
            this.refreshQueue = [];
          },
        });
      }

      this.refreshQueue.push(subscriber);
    });
  }

  public get valid(): boolean {
    return !!this.current;
  }

  public get credential(): Credential {
    return this.current;
  }
}
